/**
 * deliverables-client-route.js — client-saas /deliverables for the MANAGED
 * CLIENT persona (main.js branches this route on getUiMode(): resellers and
 * admins get deliverables-route.js, everyone else lands here).
 *
 * The client-side mirror of the reseller's status page: the plans their
 * account manager sent, and where each one stands from THEIR side of the
 * table — Pending your review, Approved, Returned with edits. Read-only plus
 * a Review action that opens the deliverable's live link (the same page an
 * emailed link opens; the link only rides along for members whose org role
 * carries manage_team, which is who reviews on a client team anyway).
 *
 * NO NEW BACKEND. GET /m5t/v5/m5mDeliverable already owner-scopes to the
 * caller (a client member sees exactly their org's rows), the server hides
 * DRAFTs from client-org members outright (owner ruling: a client never sees
 * a plan still being written), and the list carries a derived `signed` flag
 * so Approved vs Returned never requires shipping the answers. CANCELLED is
 * filtered here — revoked plans are the reseller's history, not the client's.
 */

import AuthController from '../services/AuthController.js';
import { fmtDateTime, ensureQuestionnaireAnswerStyles } from '../../../../shared/questionnaireAnswers.js';
import { guardedFetch } from '../core/navGeneration.js';
import { isDenial, denialState, failIfNotOk } from '../core/denialSurface.js';

const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);
const apiBase = () => AuthController.getConfig?.().BASE_URL || '';

// Their side of the table, one map: status + signed → what the client sees.
function clientStatus(r) {
  if (r.status === 'SENT') return { key: 'PENDING', label: 'Pending your review' };
  if (r.status === 'SUBMITTED' && r.signed) return { key: 'APPROVED', label: 'Approved' };
  if (r.status === 'SUBMITTED') return { key: 'RETURNED', label: 'Returned with edits' };
  return null; // DRAFT never arrives (server-hidden); CANCELLED is not theirs to see
}

let stylesInjected = false;
function ensureStyles() {
  ensureQuestionnaireAnswerStyles();
  if (stylesInjected) return;
  const style = document.createElement('style');
  style.id = 'm5t-deliverables-client-styles';
  style.textContent = `
    .page-shell .dc-tablewrap { overflow-x: auto; }
    .page-shell .dc-table { width: 100%; border-collapse: collapse; font-size: 0.88rem; min-width: 560px; }
    .page-shell .dc-table th { text-align: left; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-subtle, #6C7C9A); padding: 8px 12px; border-bottom: 1px solid var(--color-border, #D4DCE8); white-space: nowrap; }
    .page-shell .dc-table td { padding: 11px 12px; border-bottom: 1px solid var(--color-surface-2, #EAF0F7); vertical-align: middle; }
    .page-shell .dc-table tr:last-child td { border-bottom: 0; }
    .page-shell .dc-name { font-weight: 600; }
    .page-shell .dc-when { color: var(--color-text-subtle, #6C7C9A); white-space: nowrap; }
    .page-shell .dc-badge { display: inline-block; white-space: nowrap; font-size: 0.66rem; font-weight: 700; letter-spacing: 0.03em; padding: 3px 9px; border-radius: 999px; background: var(--color-surface-2, #EAF0F7); color: var(--color-text-muted, #4A5A78); }
    .page-shell .dc-badge[data-s="PENDING"] { background: #FEF3C7; color: #92400E; }
    .page-shell .dc-badge[data-s="APPROVED"] { background: #DCFCE7; color: #047857; }
    .page-shell .dc-badge[data-s="RETURNED"] { background: var(--color-accent-soft, #EEF4FF); color: var(--color-accent-hover, #00558D); }
    .page-shell .dc-act { appearance: none; border: 1px solid var(--color-border, #D4DCE8); background: transparent; cursor: pointer; font: inherit; font-size: 0.78rem; font-weight: 600; color: var(--color-accent, #0072BA); padding: 5px 11px; border-radius: 8px; white-space: nowrap; }
    .page-shell .dc-act:hover { background: var(--color-accent-soft, #EEF4FF); }
    .page-shell td.dc-actcell { text-align: right; }
  `;
  document.head.appendChild(style);
  stylesInjected = true;
}

const emptyState = (icon, msg) =>
  `<div class="m5t-empty"><span class="material-symbols-outlined" aria-hidden="true">${icon}</span><p>${msg}</p></div>`;

/** The per-row action, when the caller was handed the link and it is actionable. */
function actionHtml(r, cs) {
  if (!r.url && cs.key === 'PENDING') return '';
  if (cs.key === 'PENDING') return `<button class="dc-act" type="button" data-url="${esc(r.url)}">Review</button>`;
  if (cs.key !== 'APPROVED' && r.url && r.editability?.editable) {
    return `<button class="dc-act" type="button" data-url="${esc(r.url)}">Update your review</button>`;
  }
  return '';
}

function rowHTML(r) {
  const cs = clientStatus(r);
  return `<tr>
    <td><span class="dc-name">${esc(r.template_name || r.kind || 'Deliverable')}</span>${Number(r.revision) > 1 ? `<span class="dc-when" style="margin-left:6px">Rev ${esc(r.revision)}</span>` : ''}</td>
    <td><span class="dc-badge" data-s="${esc(cs.key)}">${esc(cs.label)}</span></td>
    <td class="dc-when">${r.sent_tz ? esc(fmtDateTime(r.sent_tz)) : '—'}</td>
    <td class="dc-when">${r.submitted_tz ? esc(fmtDateTime(r.submitted_tz)) : '—'}</td>
    <td class="dc-actcell">${actionHtml(r, cs)}</td>
  </tr>`;
}

export function render() {
  ensureStyles();
  return `
    <div class="page-shell" data-section="deliverables-client">
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Deliverables</h1>
        </div>
      </div>
      <div class="dashboard-card">
        <div class="card-header">
          <h2 class="card-title"><span class="material-symbols-outlined" aria-hidden="true">approval</span> Your deliverables</h2>
        </div>
        <div class="card-body" id="dc-body">${emptyState('hourglass_empty', 'Loading…')}</div>
      </div>
    </div>`;
}

export async function init() {
  const body = document.getElementById('dc-body');
  if (!body) return;
  let rows;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/m5mDeliverable`);
    const json = await res.json().catch(() => ({}));
    failIfNotOk(res, json);
    if (!json.ok) throw new Error('load failed');
    rows = (json.data || []).filter((r) => clientStatus(r));
  } catch (err) {
    body.innerHTML = isDenial(err) ? denialState() : emptyState('error', 'Could not load your deliverables. Please try again.');
    return;
  }

  if (!rows.length) {
    body.innerHTML = emptyState('approval', 'Nothing to review right now.');
    return;
  }

  body.innerHTML = `
    <div class="dc-tablewrap">
      <table class="dc-table">
        <thead><tr><th>Deliverable</th><th>Status</th><th>Received</th><th>Returned</th><th></th></tr></thead>
        <tbody id="dc-tbody">${rows.map(rowHTML).join('')}</tbody>
      </table>
    </div>`;

  body.querySelector('#dc-tbody')?.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-url]');
    if (btn?.dataset.url) window.open(btn.dataset.url, '_blank');
  });
}

export default { render, init };
